'use client';

import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import logger from '@/utils/client-logger';
import { useEffect, useMemo, useState } from 'react';
interface LensDescriptionProps {
  lensDescriptionData: {
    title: string;
    description: string;
    glasses_img: string;
    arrow_img: string;
    right_word: string;
    len_arr: Array<{
      image: string;
      word: string;
      choose_image: string;
    }>;
  };
}

export default function LensDescription({ lensDescriptionData }: LensDescriptionProps) {
  const [chooseIndex, setChooseIndex] = useState(0);
  const [imgOpacity, setImgOpacity] = useState(1);
  const [fadeIn, setFadeIn] = useState(false);

  // 处理图片切换动画
  useEffect(() => {
    if (lensDescriptionData?.len_arr?.[chooseIndex]?.choose_image) {
      setImgOpacity(0);
      setFadeIn(false);

      const timer = setTimeout(() => {
        setFadeIn(true);
        setImgOpacity(1);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [chooseIndex]);

  const chooseImg = useMemo(() => {
    try {
      return lensDescriptionData.len_arr[chooseIndex]?.choose_image || '';
    } catch (error) {
      logger.error('Failed to get chooseImg:', error);
      return '';
    }
  }, [chooseIndex]);

  const handleLensHover = (index: number) => {
    setChooseIndex(index);
  };

  const handleLensOut = () => {
    setChooseIndex(0);
  };

  // 替换图标
  const replaceIcons = (content: string) => {
    try {
      if (typeof content !== 'string') return content;

      return content
        .replace(/<i class="iconfont icon-shangbiao"><\/i>/g, '®')
        .replace(/<i class="iconfont icon-line-trademarkshangbiao"><\/i>/g, '™');
    } catch (error) {
      logger.error('Failed to replace icons:', error);
      return content;
    }
  };

  if (!lensDescriptionData?.len_arr?.length) {
    return null;
  }

  // PC端版本
  const PCVersion = () => (
    <div className="w-full bg-white min-w-[768px]">
      <div className="flex justify-between items-center max-w-[1920px] mx-auto px-[80px] py-[60px] lg:px-[120px] lg:py-[80px] 2xl:px-[150px] 2xl:py-[100px] 2xl:pb-[85px] box-border">
        {/* 左侧内容 */}
        <div className="w-[51vw] pr-[30px] lg:pr-[35px] 2xl:pr-0">
          <h1 className="mb-[30px] 2xl:mb-[40px] text-[36px] lg:text-[42px] 2xl:text-[50px] font-bs text-primary-black">
            {lensDescriptionData.title}
          </h1>
          <div
            className="text-[16px] leading-[28px] lg:text-[18px] lg:leading-[32px] 2xl:text-[20px] 2xl:leading-[36px] text-secondary-black [&_sup]:text-[0.6em] [&_sup]:relative [&_sup]:-top-[0.5em]"
            dangerouslySetInnerHTML={{ __html: replaceIcons(lensDescriptionData.description) }}
          />

          {/* 镜片选项 */}
          <ul className="flex items-center mt-[30px] lg:mt-[35px] 2xl:mt-[40px]">
            {lensDescriptionData.len_arr.map((item, index) => (
              <li
                key={index}
                className={`relative w-[120px] lg:w-[150px] 2xl:w-[180px] text-center mr-[20px] lg:mr-[30px] 2xl:mr-[40px] last:mr-0 cursor-pointer opacity-70 transition-opacity duration-300 ease-in-out ${
                  chooseIndex === index ? 'opacity-100' : ''
                }`}
                onMouseEnter={() => handleLensHover(index)}
                onMouseLeave={handleLensOut}
              >
                <Image
                  src={item.image}
                  alt={item.word}
                  width={75}
                  height={75}
                  className="w-[45px] lg:w-[60px] 2xl:w-[75px] h-auto mx-auto mb-[12px] lg:mb-[16px] 2xl:mb-[20px]"
                />
                <p className="text-[14px] lg:text-[16px] 2xl:text-[20px]">{item.word}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* 右侧眼镜展示 */}
        <div className="w-[26.25vw]">
          <div className="relative w-full">
            {/* 背景镜片图片 */}
            <Image
              src={chooseImg}
              alt="lens background"
              width={504}
              height={160}
              className={`w-full h-auto transition-opacity ${
                fadeIn ? 'duration-800' : 'duration-100'
              } ease-in-out`}
              style={{ opacity: imgOpacity }}
              priority
            />
            {/* 眼镜框架图片 */}
            <Image
              src={lensDescriptionData.glasses_img}
              alt="glasses frame"
              width={504}
              height={160}
              className="absolute top-0 left-0 w-full h-full object-contain z-[1]"
              priority
            />
          </div>

          {/* 箭头 */}
          <div className="my-[20px] mb-[8px] lg:my-[25px] lg:mb-[9px] 2xl:my-[30px] 2xl:mb-[10px]">
            <Image
              src={lensDescriptionData.arrow_img}
              alt="arrow"
              width={504}
              height={18}
              className="w-full h-auto"
              priority
              unoptimized
            />
          </div>

          {/* 右侧文字 */}
          <p className="text-center text-[14px] leading-[20px] lg:text-[16px] lg:leading-[24px] 2xl:text-[20px] 2xl:leading-[32px]">
            {lensDescriptionData.right_word}
          </p>
        </div>
      </div>
    </div>
  );

  // 移动端版本
  const MobileVersion = () => (
    <div className="w-full bg-white px-[14px] py-[23px] pb-[25px] box-border">
      {/* 顶部内容 */}
      <div className="mb-[15px]">
        <h1 className="mb-[9px] text-[25px] leading-[30px] font-bs text-primary-black">
          {lensDescriptionData.title}
        </h1>
        <div
          className="text-[12px] leading-[20px] [&_sup]:text-[0.6em] [&_sup]:relative [&_sup]:-top-[0.5em]"
          dangerouslySetInnerHTML={{ __html: replaceIcons(lensDescriptionData.description) }}
        />
      </div>

      {/* 中间部分 */}
      <div className="flex flex-col items-center">
        <div className="relative w-[280px] h-[97px]">
          {/* 背景镜片图片 */}
          <img
            src={chooseImg}
            alt="lens background"
            className={`w-[280px] h-full object-contain ${
              imgOpacity === 0 ? 'opacity-0' : 'opacity-100'
            } ${
              fadeIn ? 'transition-opacity duration-3000' : 'transition-opacity duration-3000'
            } ease-in-out`}
          />
          {/* 眼镜框架图片 */}
          <img
            src={lensDescriptionData.glasses_img}
            alt="glasses frame"
            className="absolute top-0 left-0 w-[280px] h-full object-contain z-[1]"
          />
        </div>

        <div className="w-[280px] h-[11px] mt-[13px] mb-[8px]">
          {/* <Image
            src={lensDescriptionData.arrow_img}
            alt="arrow"
            width={280}
            height={11}
            className="w-[280px] h-[11px]"
            priority
            unoptimized
          /> */}
          <img src={lensDescriptionData.arrow_img} alt="arrow" className="w-[280px] h-[11px]" />
        </div>

        <p className="text-center text-[12px] leading-[16px]">{lensDescriptionData.right_word}</p>
      </div>

      {/* 镜片选项 */}
      <ul className="flex justify-center mt-[15px]">
        {lensDescriptionData.len_arr.map((item, index) => (
          <li
            key={index}
            className={`relative w-[70px] text-center cursor-pointer opacity-70 transition-opacity duration-3000 ease-in-out ${
              chooseIndex === index ? 'opacity-100' : ''
            }`}
            onClick={() => handleLensHover(index)}
          >
            {/* <Image
              src={item.image}
              alt={item.word}
              width={50}
              height={50}
              className="w-[50px] h-[50px] mx-auto mb-[6px]"
            /> */}
            <img src={item.image} alt={item.word} className="w-[50px] h-[50px] mx-auto mb-[6px]" />
            <p className="text-[12px] leading-[13px]">{item.word}</p>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <section className="w-full">
      <WebOnly>
        <PCVersion />
      </WebOnly>
      <WapOnly>
        <MobileVersion />
      </WapOnly>
    </section>
  );
}
